import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import api from '../../utils/api';
import { usePlatformAdmin } from '../../hooks/usePlatformAdmin';
import EntHero from '../../components/enterprise/EntHero';
import EntEmptyState from '../../components/enterprise/EntEmptyState';
import '../../styles/EnterpriseDashboard.css';
import { isCompanyAdmin, roleLabel } from '../../utils/companyRoles';

export default function EnterpriseSelectPage() {
  const navigate = useNavigate();
  const { isPlatformAdmin, loading: platformLoading } = usePlatformAdmin();
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/enterprise/my-enterprises')
      .then((res) => {
        if (!cancelled) setEnterprises(res.data.enterprises || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const adminEnterprises = useMemo(
    () => enterprises.filter((e) => isCompanyAdmin(e)),
    [enterprises]
  );
  const handleSelect = (id) => navigate(`/enterprise-app/${id}/dashboard`);

  if (loading) return <div className="ent-app"><div className="ent-loading">Loading…</div></div>;
  if (error) return (
    <div className="ent-app">
      <main className="ent-main">
        <div className="ent-main-inner">
          <div className="ent-flash ent-flash-error" role="alert">{error}</div>
        </div>
      </main>
    </div>
  );

  /* Backend is source of truth (GET /api/enterprise/my-enterprises). Skip the picker when there is only one admin context. */
  if (adminEnterprises.length === 1) {
    const onlyId = adminEnterprises[0]?.enterpriseId;
    if (onlyId != null && String(onlyId).trim() !== '') {
      return <Navigate to={`/enterprise-app/${onlyId}/dashboard`} replace />;
    }
  }

  return (
    <div className="ent-app">
      <main className="ent-main">
        <div className="ent-main-inner">
        <div className="ent-content">
          <EntHero
            eyebrow="Workspace"
            title="Enterprise"
            lead="Select a company you administer. New enterprise accounts are created by Velox platform administrators; enterprise admins are assigned from the platform admin area."
          />

          {!platformLoading && isPlatformAdmin ? (
            <section className="ent-section">
              <p className="ent-page-subtitle" style={{ marginBottom: '0.75rem' }}>
                You have platform admin access — manage companies and assign enterprise admins in{' '}
                <Link to="/admin/enterprise" style={{ color: 'var(--ent-accent, #3b82f6)' }}>
                  Enterprise accounts
                </Link>
                .
              </p>
            </section>
          ) : null}

          {adminEnterprises.length > 0 ? (
            <section className="ent-section">
              <h2 className="ent-section-title">Your enterprises</h2>
              <div className="ent-table-wrap">
                <table className="ent-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminEnterprises.map((e) => (
                      <tr key={e.enterpriseId}>
                        <td>{e.name}</td>
                        <td>{roleLabel(e)}</td>
                        <td>
                          {isCompanyAdmin(e) && (
                            <button type="button" className="ent-btn-primary" onClick={() => handleSelect(e.enterpriseId)}>
                              Open dashboard
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
            <section className="ent-section">
              <EntEmptyState
                title="No enterprise workspaces yet"
                lead="You are not an enterprise admin for any company yet. If your organisation uses Velox Enterprise, ask your Velox administrator to assign you as an enterprise admin, or accept your enterprise invite by email."
              />
            </section>
          )}
        </div>
        </div>
      </main>
    </div>
  );
}
